import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
    EnvironmentProviders,
    ErrorHandler,
    inject,
    LOCALE_ID,
    makeEnvironmentProviders,
    provideBrowserGlobalErrorListeners,
    provideEnvironmentInitializer,
    Provider
} from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat';
import { DefaultUrlSerializer, UrlSegmentGroup, UrlSerializer, UrlTree } from '@angular/router';

import { BaasAppInstance, BaasI18n, BaasI18nOptions, LOCALE } from '@lvflanders/bar';

import { BarErrorHandler } from '../bar-error-handler';
import { BarErrorChunkLoadFailedHook } from '../hooks/bar-error-chunk-load-failed.hook';
import { BarErrorExpressionChangedAfterCheckedHook } from '../hooks/bar-error-expression-changed-after-checked.hook';
import { baasTelemetryInterceptor } from '../interceptors/baas-telemetry.interceptor';
import { barDateTimeInterceptor } from '../interceptors/bar-date-time.interceptor';
import { BarDateTimeConversionStrategy } from '../models/bar-date-time-conversion-strategy.enum';
import { BaasCoreInitService } from '../services/baas-core-init.service';
import { _BAAS_CORE_FEATURE_ORDER, BaasCoreFeature, BaasCoreFeatureKind } from './baas-core.feature';
import { provideBaasErrorHandler } from './baas-lifecycle.providers';
import { defineAppProviders } from './define-app-providers.function';

// next is needed for case insensitive routing (added as provider in the forRoot method)
class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const urlTree = super.parse(url);
        this.#lowerCaseSegments(urlTree.root);
        return urlTree;
    }

    // https://stackoverflow.com/a/70879670
    #lowerCaseSegments(urlSegmentGroup: UrlSegmentGroup) {
        if (urlSegmentGroup.hasChildren()) {
            Object.entries(urlSegmentGroup.children).forEach(([key, value]) => this.#lowerCaseSegments(value));
        }
        urlSegmentGroup.segments.forEach((segment) => (segment.path = segment.path.toLowerCase()));
    }
}

/** Provides the default configuration and providers for BaaS. */
export function provideBaasCore(...features: BaasCoreFeature[]) {
    if (!features) features = [];
    // Providing services for the features sort by feature kind,
    // the order is important to avoid overriding providers by default providers
    // Sort the features based on the custom order
    features = features.sort((a, b) => {
        const orderA = _BAAS_CORE_FEATURE_ORDER[a.kind] ?? Number.MAX_VALUE; // Default to MAX_VALUE if not found
        const orderB = _BAAS_CORE_FEATURE_ORDER[b.kind] ?? Number.MAX_VALUE;
        return orderA - orderB;
    });
    // default providers
    const providers: (Provider | EnvironmentProviders)[] = [
        { provide: LOCALE_ID, useValue: LOCALE.FLEMISH },
        {
            provide: UrlSerializer,
            // routing needs to work case insensitive
            useClass: LowerCaseUrlSerializer
        },
        Location,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        },
        {
            provide: ErrorHandler,
            useClass: BarErrorHandler
        },
        provideBaasErrorHandler(BarErrorExpressionChangedAfterCheckedHook),
        provideBaasErrorHandler(BarErrorChunkLoadFailedHook),
        provideEnvironmentInitializer(() => inject(BaasCoreInitService).init()),
        provideBrowserGlobalErrorListeners(),
        provideSignalFormsConfig({
            classes: NG_STATUS_CLASSES
        }),
        features.map((feature) => feature.providers)
    ];
    return makeEnvironmentProviders(providers);
}

export function withDateTimeInterception(conversionStrategy: BarDateTimeConversionStrategy = BarDateTimeConversionStrategy.Iso): BaasCoreFeature {
    return {
        kind: BaasCoreFeatureKind.DateTimeInterception,
        providers: [provideHttpClient(withXhr(), withInterceptors([barDateTimeInterceptor(conversionStrategy)]))]
    };
}

/** Provides the BaaS App providers which is statefull and invokes a backend api. */
export function withBaasAppBehavior(appInstance: BaasAppInstance): BaasCoreFeature {
    return {
        kind: BaasCoreFeatureKind.BaasApp,
        providers: [
            // setup http with interceptors
            provideHttpClient(withInterceptors([baasTelemetryInterceptor]), withInterceptorsFromDi(), withFetch()),
            // in case connected we need to add extra providers
            ...defineAppProviders(appInstance.container)
        ]
    };
}

/** Whether the i18n features from \@lvflanders/bar should be integrated. Don't combine this with the function withBaasAppBehavior.*/
export function withI18n(options: BaasI18nOptions = {}): BaasCoreFeature {
    const i18n = new BaasI18n(options);
    return {
        kind: BaasCoreFeatureKind.I18n,
        providers: [{ provide: BaasI18n, useValue: i18n }]
    };
}
